import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import { ConnectionMode } from '@xyflow/system';
import '@xyflow/react/dist/style.css';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Loader2,
  GitBranch, 
  MapPin as MapIcon, 
  Building2,
  Folder,
  User,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useAssetsStore } from '@/stores/assetsStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useCloudServiceStore } from '@/stores/cloudServiceStore';
import { useCloudResourceStore } from '@/stores/cloudResourceStore';
import { useConduitStore } from '@/stores/conduitStore';
import { useInternetConnectionStore } from '@/stores/internetConnectionStore';
import { useDMZConfigStore } from '@/stores/dmzConfigStore';
import { generateContainerLayout } from '@/utils/containerLayoutGenerator';
import { AssetNode } from '@/components/assets/AssetNode';
import { SiteGroupNode } from '@/components/assets/nodes/SiteGroupNode';
import { ZoneGroupNode } from '@/components/assets/nodes/ZoneGroupNode';
import { SystemGroupNode } from '@/components/assets/nodes/SystemGroupNode';
import { CloudGroupNode } from '@/components/assets/nodes/CloudGroupNode';
import { InternetNode } from '@/components/assets/nodes/InternetNode';
import { DMZNode } from '@/components/assets/nodes/DMZNode';
import { EndpointNode } from '@/components/assets/nodes/EndpointNode';
import { CloudResourceNode } from '@/components/assets/nodes/CloudResourceNode';
import { ConduitEdge } from '@/components/assets/edges/ConduitEdge';
import { NodeCreateDialog } from '@/components/assets/NodeCreateDialog';
import { InspectorDrawer } from '@/components/assets/InspectorDrawer';
import { AssetBrowser } from '@/components/assets/AssetBrowser';
import AssetsMapView from '@/components/assets/AssetsMapView';
import { LayoutToolsPane } from '@/components/assets/LayoutToolsPane';
import { cn } from '@/lib/utils';
import { NodeBase, EdgeBase } from '@/types/assets';
import type { Endpoint } from '@/types/endpoint';
import { toast } from 'sonner';

const nodeTypes = {
  asset: AssetNode,
  siteGroup: SiteGroupNode,
  zoneGroup: ZoneGroupNode,
  systemGroup: SystemGroupNode,
  cloudGroup: CloudGroupNode,
  internet: InternetNode,
  dmz: DMZNode,
  endpoint: EndpointNode,
  cloudResource: CloudResourceNode,
};

const edgeTypes = {
  conduit: ConduitEdge,
};



function AssetsFlow({ focusEntityId, focusEntityType, onViewModeChange }: { focusEntityId?: string; focusEntityType?: EntityType; onViewModeChange?: (mode: string) => void }) {
  const { getNodes } = useReactFlow();
  const { 
    edges: storeEdges, 
    updateNodePosition,
    loadAssets,
    loadRisks,
    loadEvidence,
    selectedNodeId,
    setSelectedNode
  } = useAssetsStore();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeBase>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<EdgeBase>>([]);
  const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [isLayouting, setIsLayouting] = useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // Handle node position and dimension changes
  const handleNodesChange = useCallback((changes: any[]) => {
    // Handle dimension changes (resize completed)
    const dimensionChanges = changes.filter(
      (change) => change.type === 'dimensions' && !change.resizing && change.dimensions
    );
    
    if (dimensionChanges.length > 0) {
      setNodes((nds) => 
        nds.map((node) => {
          const dimChange = dimensionChanges.find((c: any) => c.id === node.id);
          if (dimChange) {
            return {
              ...node,
              style: {
                ...node.style,
                width: dimChange.dimensions.width,
                height: dimChange.dimensions.height,
              },
            };
          }
          return node;
        })
      );
    }

    // Apply all standard changes (position, selection, etc.)
    onNodesChange(changes);
    
    // Save position changes to database
    changes.forEach((change) => {
      if (change.type === 'position' && change.dragging === false && change.position) {
        updateNodePosition(change.id, change.position);
      }
    });
  }, [onNodesChange, updateNodePosition, setNodes]);

  // Sync store edges to React Flow state
  useEffect(() => {
    setEdges(storeEdges);
  }, [storeEdges, setEdges]);

  // Load stores
  const { sites, loadSites } = useSiteStore();
  const { zones, loadZones } = useZoneStore();
  const { systems, loadSystems } = useSystemStore();
  const { cloudServices, loadCloudServices } = useCloudServiceStore();
  const { cloudResources, loadCloudResources } = useCloudResourceStore();
  const { conduits, loadConduits } = useConduitStore();
  const { connections: internetConnections, loadConnections: loadInternetConnections } = useInternetConnectionStore();
  const { configs: dmzConfigs, loadConfigs: loadDMZConfigs } = useDMZConfigStore();
  
  // Endpoints state for layout generation
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  
  // Load endpoints directly from Supabase
  const loadEndpoints = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.org_id) return;

      const { data, error } = await supabase
        .from('endpoints')
        .select('*')
        .eq('org_id', profile.org_id);

      if (error) throw error;
      setEndpoints((data || []) as Endpoint[]);
    } catch (error) {
      console.error('Error loading endpoints:', error);
    }
  }, []);

  // Save layout to org_layout table (shared across org)
  const saveOrgLayout = useCallback(async (nodesToSave: Node<NodeBase>[], edgesToSave: Edge<EdgeBase>[]) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.org_id) return;

      // Check if layout exists
      const { data: existing } = await supabase
        .from('org_layout')
        .select('id')
        .eq('org_id', profile.org_id)
        .single();

      const layoutData = {
        nodes: nodesToSave.map(n => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: n.data,
          parentId: n.parentId,
          extent: n.extent,
          style: n.style,
        })) as any,
        edges: edgesToSave.map(e => ({
          id: e.id,
          type: e.type,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          data: e.data,
        })) as any,
        updated_by: user.id,
      };

      if (existing) {
        // Update existing layout
        await supabase
          .from('org_layout')
          .update(layoutData)
          .eq('org_id', profile.org_id);
      } else {
        // Insert new layout
        await supabase
          .from('org_layout')
          .insert({ ...layoutData, org_id: profile.org_id });
      }

      console.log('✅ Org layout saved');
    } catch (error) {
      console.error('❌ Failed to save org layout:', error);
    }
  }, []);

  // Load layout from org_layout table
  const loadOrgLayout = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.org_id) return;

      const { data: layout } = await supabase
        .from('org_layout')
        .select('nodes, edges, viewport')
        .eq('org_id', profile.org_id)
        .single();

      if (layout?.nodes && layout?.edges) {
        const layoutNodes = layout.nodes as any[];
        const layoutEdges = layout.edges as any[];
        
        const loadedNodes = layoutNodes.map((n: any) => ({
          id: n.id,
          type: n.type || 'asset',
          position: n.position,
          data: n.data,
          parentId: n.parentId,
          extent: n.extent,
          style: n.style,
        }));
        
        const loadedEdges = layoutEdges.map((e: any) => ({
          id: e.id,
          type: e.type || 'default',
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          data: e.data,
        }));

        setNodes(loadedNodes);
        setEdges(loadedEdges);
        console.log('✅ Org layout loaded:', loadedNodes.length, 'nodes');
      }
    } catch (error) {
      console.error('❌ Failed to load org layout:', error);
    }
  }, [setNodes, setEdges]);

  // Initialize data on first load
  useEffect(() => {
    loadAssets();
    loadRisks();
    loadEvidence();
    loadSites();
    loadZones();
    loadSystems();
    loadCloudServices();
    loadCloudResources();
    loadConduits();
    loadInternetConnections();
    loadDMZConfigs();
    loadEndpoints();
    loadOrgLayout();
  }, [loadAssets, loadRisks, loadEvidence, loadSites, loadZones, loadSystems, loadCloudServices, loadCloudResources, loadConduits, loadInternetConnections, loadDMZConfigs, loadEndpoints, loadOrgLayout]);

  // Auto-save layout changes (debounced)
  const [_lastSavedBy, setLastSavedBy] = useState<string | null>(null);
  useEffect(() => {
    if (nodes.length > 0) {
      const timer = setTimeout(() => {
        saveOrgLayout(nodes, edges);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [nodes, edges, saveOrgLayout]);

  // Real-time subscription for org layout changes from other users
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    
    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.org_id) return;

      channel = supabase
        .channel('org-layout-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'org_layout',
            filter: `org_id=eq.${profile.org_id}`,
          },
          (payload) => {
            const newPayload = payload.new as any;
            // Only update if another user made the change
            if (newPayload.updated_by !== user.id) {
              const layoutNodes = (newPayload.nodes || []) as any[];
              const layoutEdges = (newPayload.edges || []) as any[];
              
              const newNodes = layoutNodes.map((n: any) => ({
                id: n.id,
                type: n.type || 'asset',
                position: n.position,
                data: n.data,
                parentId: n.parentId,
                extent: n.extent,
                style: n.style,
              }));
              
              const newEdges = layoutEdges.map((e: any) => ({
                id: e.id,
                type: e.type || 'default',
                source: e.source,
                target: e.target,
                data: e.data,
              }));

              setNodes(newNodes);
              setEdges(newEdges);
              setLastSavedBy(newPayload.updated_by);
              toast.info('Layout updated by another user');
            }
          }
        )
        .subscribe();
    };

    setupRealtimeSubscription();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [setNodes, setEdges]);

  // Focus on entity when navigating from asset browser
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (focusEntityId && nodes.length > 0) {
      // Match the node ID format used by generateNodesFromHierarchy (e.g., "site-123", "zone-456")
      const nodeToFocus = nodes.find(n => 
        n.id === focusEntityId || 
        n.id === `site-${focusEntityId}` ||
        n.id === `zone-${focusEntityId}` ||
        n.id === `system-${focusEntityId}` ||
        n.id.includes(focusEntityId)
      );
      if (nodeToFocus) {
        setSelectedNode(nodeToFocus.id);
        setInspectorOpen(true);
        
        // Center view on the node
        setTimeout(() => {
          fitView({
            nodes: [{ id: nodeToFocus.id }],
            duration: 800,
            padding: 0.5,
          });
        }, 100);
      } else {
        toast.info(`Entity not found in layout. Try adding it to the diagram first.`);
      }
    }
  }, [focusEntityId, focusEntityType, nodes, fitView, setSelectedNode]);

  // Removed auto-save that was conflicting with Supabase data

  const onConnect = useCallback(
    (params: Connection) => {
      console.log('Connection attempt:', params);
      
      // Check if connection already exists between these nodes
      const existingEdge = edges.find(e => 
        (e.source === params.source && e.target === params.target) ||
        (e.source === params.target && e.target === params.source)
      );
      
      if (existingEdge) {
        toast.info('Connection already exists between these nodes');
        return;
      }
      
      const edgeId = `edge-${params.source}-${params.target}-${Date.now()}`;
      const newEdge: Edge<EdgeBase> = {
        id: edgeId,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: 'conduit',
        data: {
          id: edgeId,
          type: 'connected_to',
          source: params.source!,
          target: params.target!,
          // Don't set default label - keeps edge clean without blur
          direction: 'Bidirectional',
          encryption: false,
          inspection: null,
        },
      };
      
      // Only add to React Flow state - don't add to store to avoid duplicates
      setEdges((eds) => addEdge(newEdge, eds));
      toast.success('Connection created');
    },
    [setEdges, edges]
  );

  const onPaneClick = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.classList.contains('react-flow__pane')) {
      setSelectedNode(null);
      setSelectedEdgeId(null);
      setInspectorOpen(false);
      // Clear edge highlight styling
      setEdges((eds) => eds.map(e => ({
        ...e,
        style: { ...e.style, stroke: undefined, strokeWidth: undefined },
      })));
    }
  }, [setSelectedNode, setEdges]);

  // Single click on edge: only select/highlight
  const handleEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation();
    setSelectedEdgeId(edge.id);
    setSelectedNode(null);
    // Update edge styling to show selection (highlighted in bright orange for visibility)
    setEdges((eds) => eds.map(e => ({
      ...e,
      style: e.id === edge.id 
        ? { ...e.style, stroke: '#f97316', strokeWidth: 4 } 
        : { ...e.style, stroke: undefined, strokeWidth: undefined },
    })));
  }, [setEdges, setSelectedNode]);

  // Delete selected edge via toolbar button
  const handleDeleteSelectedEdge = useCallback(() => {
    if (selectedEdgeId) {
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdgeId));
      setSelectedEdgeId(null);
      toast.success('Connection deleted');
    }
  }, [selectedEdgeId, setEdges]);

  // Single click on node: only select (don't open inspector)
  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node.id);
    setSelectedEdgeId(null);
    // Clear edge highlight
    setEdges((eds) => eds.map(e => ({
      ...e,
      style: { ...e.style, stroke: undefined, strokeWidth: undefined },
    })));
  }, [setSelectedNode, setEdges]);

  // Double click on node: open inspector
  const handleNodeDoubleClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node.id);
    setInspectorOpen(true);
  }, [setSelectedNode]);

  const handleGenerateHierarchy = useCallback(async () => {
    setIsLayouting(true);
    
    try {
      // Preserve special nodes (Internet, DMZ) that aren't from database
      const specialNodes = nodes.filter(n => n.type === 'internet' || n.type === 'dmz');
      
      // Preserve ALL manually created edges (not just special node edges)
      // Keep edges that aren't conduits from database - they will be regenerated
      const preservedEdges = edges.filter(e => !e.id.startsWith('conduit-'));
      
      const containerNodes = generateContainerLayout({
        sites,
        zones,
        systems,
        cloudServices,
        cloudResources,
        endpoints,
        internetConnections,
        dmzConfigs,
        existingNodes: nodes,
      });
      
      // Generate conduit edges from database
      const conduitEdges: Edge[] = conduits.map(conduit => {
        const srcZoneNode = containerNodes.find((n: Node) => n.id === `zone-${conduit.src_zone_id}`);
        const dstZoneNode = containerNodes.find((n: Node) => n.id === `zone-${conduit.dst_zone_id}`);
        
        if (!srcZoneNode || !dstZoneNode) return null;
        
        return {
          id: `conduit-${conduit.id}`,
          source: srcZoneNode.id,
          target: dstZoneNode.id,
          type: 'conduit',
          data: {
            label: conduit.name,
            direction: conduit.direction,
            ports: conduit.ports_protocols,
            encryption: conduit.encryption,
            inspection: conduit.inspection,
          },
        };
      }).filter(Boolean) as Edge[];
      
      // Merge special nodes with generated containers
      const allNodes = [...containerNodes, ...specialNodes];
      
      // Merge conduit edges with all preserved manual edges
      const allEdges = [...conduitEdges, ...preservedEdges];
      
      setNodes(allNodes as any);
      setEdges(allEdges as any);
      
      // Save layout to org
      await saveOrgLayout(allNodes as any, allEdges as any);
      
      // Auto fit-to-view after layout generation with smooth animation
      setTimeout(() => {
        fitView({ 
          padding: 0.12, 
          duration: 800,
          maxZoom: 1.2,
        });
      }, 150);
      
      // Show detailed success message
      const totalAssets = sites.length + zones.length + systems.length + endpoints.length + cloudServices.length + cloudResources.length;
      toast.success(`Layout generated: ${sites.length} sites, ${zones.length} zones, ${systems.length} systems, ${endpoints.length} endpoints`, {
        description: `${totalAssets} total assets arranged in optimized grid layout`,
        duration: 4000,
      });
    } catch (error) {
      console.error('Layout generation failed:', error);
      toast.error('Failed to generate layout');
    } finally {
      setIsLayouting(false);
    }
  }, [sites, zones, systems, cloudServices, cloudResources, endpoints, conduits, nodes, edges, setNodes, setEdges, saveOrgLayout, fitView]);

  const handleExportPNG = useCallback(() => {
    toast.info('PNG export functionality would be implemented here');
  }, []);

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.12, duration: 500 });
  }, [fitView]);

  // Get selected node for tools pane context
  const selectedNodeForTools = nodes.find(n => n.id === selectedNodeId) || null;

  // Update node data (used by InspectorDrawer after editing)
  const handleUpdateNodeData = useCallback((nodeId: string, data: Record<string, any>) => {
    setNodes((nds) => nds.map((n) => {
      if (n.id === nodeId) {
        return { ...n, data: { ...n.data, ...data } };
      }
      return n;
    }));
  }, [setNodes]);

  // Helper to check if a position is inside a container
  const isInsideContainer = useCallback((pos: { x: number; y: number }, container: Node) => {
    const width = (container.style?.width as number) || 500;
    const height = (container.style?.height as number) || 500;
    return (
      pos.x >= container.position.x &&
      pos.x <= container.position.x + width &&
      pos.y >= container.position.y &&
      pos.y <= container.position.y + height
    );
  }, []);

  // Handle drag stop to attach Internet/DMZ nodes to sites
  const handleNodeDragStop = useCallback((_event: React.MouseEvent, node: Node) => {
    console.log('Drag stopped:', { type: node.type, id: node.id, position: node.position, parentId: node.parentId });
    
    if (node.type === 'internet' || node.type === 'dmz') {
      // Get fresh nodes from React Flow
      const currentNodes = getNodes();
      console.log('Looking for siteGroup containers...', currentNodes.filter(n => n.type === 'siteGroup'));
      
      // Check if dropped inside any siteGroup
      const parentSite = currentNodes.find(n => {
        if (n.type !== 'siteGroup') return false;
        const inside = isInsideContainer(node.position, n);
        console.log(`Checking if inside ${n.data.label}:`, { 
          nodePos: node.position, 
          sitePos: n.position, 
          siteSize: { w: n.style?.width, h: n.style?.height },
          inside 
        });
        return inside;
      });
      
      console.log('Parent site found:', parentSite?.data.label);
      
      if (parentSite && !node.parentId) {
        // Convert to relative position and set parent
        const relativePosition = {
          x: node.position.x - parentSite.position.x,
          y: node.position.y - parentSite.position.y,
        };
        console.log('Attaching node with relative position:', relativePosition);
        
        setNodes(nds => nds.map(n => {
          if (n.id === node.id) {
            return {
              ...n,
              parentId: parentSite.id,
              extent: 'parent' as const,
              position: relativePosition,
            };
          }
          return n;
        }));
        toast.success(`Node attached to ${parentSite.data.label}`);
      } else if (!parentSite) {
        console.log('No parent site found - node remains free-floating');
      } else {
        console.log('Node already has parent:', node.parentId);
      }
    }
  }, [setNodes, isInsideContainer, getNodes]);

  // Empty state check
  const isEmpty = sites.length === 0 && cloudServices.length === 0;

  return (
    <div className="h-full flex relative">
      {/* Empty State Overlay */}
      {isEmpty && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8 bg-background/80 backdrop-blur-sm">
          <GitBranch className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No assets to visualize</h3>
          <p className="text-sm text-muted-foreground mb-6 text-center max-w-md">
            Create sites and assets in the Asset Browser to see them in the System Layout
          </p>
          <Button onClick={() => onViewModeChange?.('browser')}>
            <Building2 className="h-4 w-4 mr-2" />
            Go to Asset Browser
          </Button>
        </div>
      )}
      
      {/* Left Toolbar - LayoutToolsPane */}
      <div className={cn(isEmpty && "opacity-50 pointer-events-none")}>
        <LayoutToolsPane
          selectedNode={selectedNodeForTools}
          selectedEdgeId={selectedEdgeId}
          isLayouting={isLayouting}
          onAddSite={() => setNodeDialogOpen(true)}
          onAddCloudService={() => setNodeDialogOpen(true)}
          onAddZone={() => setNodeDialogOpen(true)}
          onAddSystem={() => setNodeDialogOpen(true)}
          onAddEndpoint={() => setNodeDialogOpen(true)}
          onAddCloudResource={() => setNodeDialogOpen(true)}
          onAddInternet={() => setNodeDialogOpen(true)}
          onAddDMZ={() => setNodeDialogOpen(true)}
          onGenerateFromBrowser={handleGenerateHierarchy}
          onDeleteEdge={handleDeleteSelectedEdge}
          onFitView={handleFitView}
          onExportPNG={handleExportPNG}
        />
      </div>

      {/* React Flow Canvas */}
      <div className="flex-1 relative">
        {/* Enhanced Loading Overlay */}
        {isLayouting && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 animate-fade-in">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="font-semibold">Generating Layout...</p>
            </div>
          </div>
        )}
        
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onPaneClick={onPaneClick}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          onEdgeClick={handleEdgeClick}
          onNodeDragStop={handleNodeDragStop}
          nodeTypes={nodeTypes as any}
          edgeTypes={edgeTypes as any}
          connectionMode={ConnectionMode.Loose}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          minZoom={0.2}
          maxZoom={2}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
          nodesDraggable={true}
        >
          <Background />
          <Controls />
          <MiniMap 
            nodeStrokeColor="#374151"
            nodeColor="#6b7280"
            maskColor="rgba(0, 0, 0, 0.1)"
          />
        </ReactFlow>

        {/* Status Bar */}
        <div className="absolute bottom-4 left-4 bg-background border border-border rounded-md px-3 py-2 shadow-md">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{nodes.length} nodes</span>
            <span>{edges.length} edges</span>
            {selectedNodeId && (
              <Badge variant="outline" className="text-xs">
                Selected: {nodes.find(n => n.id === selectedNodeId)?.data.label}
              </Badge>
            )}
            {selectedEdgeId && (
              <Badge variant="outline" className="text-xs bg-danger/10 text-danger border-danger/30">
                Connection selected
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs and Drawers */}
      <NodeCreateDialog
        open={nodeDialogOpen}
        onOpenChange={setNodeDialogOpen}
      />

      <InspectorDrawer 
        open={inspectorOpen} 
        onOpenChange={setInspectorOpen}
        nodes={nodes}
        onUpdateNodeData={handleUpdateNodeData}
      />
    </div>
  );
}

import { EntityType } from '@/types/link';

function AssetsPage() {
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAssigned = searchParams.get('filter') === 'assigned';
  
  // Determine view: URL param > User preference > Default
  const viewFromUrl = searchParams.get('view') as 'browser' | 'map' | 'layout' | null;
  const defaultView = profile?.preferences?.assets_default_view || 'browser';
  const [viewMode, setViewMode] = useState<'browser' | 'map' | 'layout'>(viewFromUrl || defaultView as any);
  const [focusEntityId, setFocusEntityId] = useState<string | undefined>();
  const [focusEntityType, setFocusEntityType] = useState<EntityType | undefined>();
  
  // Handle navigation to layout view with entity selection
  const handleViewInLayout = useCallback((entityId: string, entityType: EntityType) => {
    setFocusEntityId(entityId);
    setFocusEntityType(entityType);
    setSearchParams({ view: 'layout', focus: entityId });
    setViewMode('layout');
  }, [setSearchParams]);
  
  // Update URL when view changes
  useEffect(() => {
    if (viewMode !== viewFromUrl) {
      setSearchParams({ view: viewMode });
    }
  }, [viewMode, viewFromUrl, setSearchParams]);
  
  return (
    <div className="h-screen flex flex-col">
      <div className="border-b border-border p-6 space-y-4">
        <div>
          <h1 className="text-3xl font-bold">Assets Management</h1>
          <p className="text-muted-foreground">
            Interactive asset management with risk assessment and task tracking
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as any)}>
            <TabsList>
              <TabsTrigger value="browser" className="gap-2">
                <Folder className="h-4 w-4" />
                Asset Browser
              </TabsTrigger>
              <TabsTrigger value="map" className="gap-2">
                <MapIcon className="h-4 w-4" />
                Map View
              </TabsTrigger>
              <TabsTrigger value="layout" className="gap-2">
                <GitBranch className="h-4 w-4" />
                System Layout
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Button
            variant={filterAssigned ? "default" : "outline"}
            size="sm"
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              if (filterAssigned) {
                newParams.delete('filter');
              } else {
                newParams.set('filter', 'assigned');
              }
              setSearchParams(newParams);
            }}
          >
            <User className="h-4 w-4 mr-2" />
            Assigned to me
          </Button>
        </div>
      </div>
      
      <div className="flex-1 overflow-hidden">
        {viewMode === 'browser' && (
          <div className="h-full p-6">
            <AssetBrowser onViewInLayout={handleViewInLayout} filterByOwner={filterAssigned ? profile?.user_id : undefined} />
          </div>
        )}
        {viewMode === 'map' && (
          <AssetsMapView />
        )}
        {viewMode === 'layout' && (
          <ReactFlowProvider>
            <AssetsFlow 
              focusEntityId={focusEntityId} 
              focusEntityType={focusEntityType}
              onViewModeChange={(mode) => setViewMode(mode as 'browser' | 'map' | 'layout')}
            />
          </ReactFlowProvider>
        )}
      </div>
    </div>
  );
}

export default AssetsPage;